import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSession } from "@/auth/session";
import SistemaTab from "@/pages/configuracion/components/SistemaTab";
import UsuariosTab from "@/pages/configuracion/components/UsuariosTab";
import TarifasTab from "@/pages/configuracion/components/TarifasTab";
import NotificacionesTab from "@/pages/configuracion/components/NotificacionesTab";
import SeguridadTab from "@/pages/configuracion/components/SeguridadTab";
import SectoresTab from "@/pages/configuracion/components/SectoresTab";
import GastosTab from "@/pages/configuracion/components/GastosTab";
import DispositivosTab from "@/pages/configuracion/components/DispositivosTab";
import FormasPagoTab from "@/pages/configuracion/components/FormasPagoTab";
import DTETab from "@/pages/configuracion/components/DTETab";
import BoletaTab from "@/pages/configuracion/components/BoletaTab";
import BodegasTab from "@/pages/configuracion/components/BodegasTab";
import SuscripcionTab from "@/pages/configuracion/components/SuscripcionTab";
import OTTab from "@/pages/configuracion/components/OTTab";

const ALL_TABS = [
  { id: "sistema", label: "Sistema", icon: "ri-settings-3-line", saasOnly: false, requiresModule: null },
  { id: "suscripcion", label: "Suscripción", icon: "ri-secure-payment-line", saasOnly: true, requiresModule: null },
  { id: "usuarios", label: "Usuarios y Roles", icon: "ri-team-line", saasOnly: false, requiresModule: null },
  { id: "tarifas", label: "Tarifas", icon: "ri-price-tag-3-line", saasOnly: false, requiresModule: null },
  { id: "sectores", label: "Sectores", icon: "ri-map-pin-2-line", saasOnly: false, requiresModule: null },
  { id: "gastos", label: "Gastos", icon: "ri-money-dollar-circle-line", saasOnly: false, requiresModule: null },
  { id: "dispositivos", label: "Dispositivos", icon: "ri-smartphone-line", saasOnly: false, requiresModule: null },
  { id: "bodegas", label: "Bodegas", icon: "ri-store-2-line", saasOnly: false, requiresModule: null },
  { id: "formas_pago", label: "Formas de pago", icon: "ri-bank-card-line", saasOnly: false, requiresModule: null },
  { id: "boleta", label: "Boleta", icon: "ri-receipt-line", saasOnly: false, requiresModule: null },
  { id: "dte", label: "DTE", icon: "ri-file-settings-line", saasOnly: false, requiresModule: null },
  { id: "notificaciones", label: "Notificaciones", icon: "ri-notification-3-line", saasOnly: false, requiresModule: null },
  { id: "seguridad", label: "Seguridad", icon: "ri-shield-check-line", saasOnly: false, requiresModule: null },
  { id: "ot", label: "OT", icon: "ri-tools-line", saasOnly: false, requiresModule: "operaciones" },
];

export default function ConfiguracionPage() {
  const { profile, session, hasModule } = useSession();
  const [searchParams] = useSearchParams();

  // Tab Suscripción solo aparece cuando el tenant está registrado en el catálogo (planCode != null)
  const planCode = profile?.planCode ?? session?.planCode ?? null;
  const isSaas = planCode !== null;

  const tabs = useMemo(
    () => ALL_TABS.filter((t) => (!t.saasOnly || isSaas) && (!t.requiresModule || hasModule(t.requiresModule))),
    [isSaas, hasModule],
  );

  const initialTab = searchParams.get("payment") && isSaas ? "suscripcion" : "sistema";
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h1>
        <p className="text-sm text-gray-500 mt-1">
          Administra los parámetros generales, usuarios, tarifas y preferencias del sistema APR.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar tabs */}
        <aside className="w-52 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors cursor-pointer border-l-4 ${
                  activeTab === tab.id
                    ? "bg-blue-50 text-blue-700 border-blue-500 font-semibold"
                    : "text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={tab.icon} />
                </div>
                <span className="whitespace-nowrap">{tab.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {activeTab === "sistema" && <SistemaTab />}
          {activeTab === "suscripcion" && isSaas && <SuscripcionTab />}
          {activeTab === "usuarios" && <UsuariosTab />}
          {activeTab === "tarifas" && <TarifasTab />}
          {activeTab === "sectores" && <SectoresTab />}
          {activeTab === "gastos" && <GastosTab />}
          {activeTab === "dispositivos" && <DispositivosTab />}
          {activeTab === "bodegas" && <BodegasTab />}
          {activeTab === "formas_pago" && <FormasPagoTab />}
          {activeTab === "boleta" && <BoletaTab />}
          {activeTab === "dte" && <DTETab />}
          {activeTab === "notificaciones" && <NotificacionesTab />}
          {activeTab === "seguridad" && <SeguridadTab />}
          {activeTab === "ot" && <OTTab />}
        </div>
      </div>
    </div>
  );
}
